import React, { useEffect, useRef, useState } from 'react';

// Icons for gold background — use white + navy fills for contrast
const DollarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="17" fill="#0B1F3B"/>
    <circle cx="20" cy="20" r="13" fill="white" fillOpacity="0.15"/>
    <text x="20" y="27" textAnchor="middle" fontSize="20" fill="white" fontWeight="bold">$</text>
  </svg>
);

const BarChartGrowthIcon = () => (
  <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="24" width="7" height="12" rx="2" fill="#0B1F3B"/>
    <rect x="14" y="17" width="7" height="19" rx="2" fill="#0B1F3B"/>
    <rect x="25" y="10" width="7" height="26" rx="2" fill="#0B1F3B"/>
    <path d="M5 22L16 14L27 8L36 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="36" cy="4" r="3" fill="white"/>
  </svg>
);

const StockMarketIcon = () => (
  <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="36" height="24" rx="3" fill="#0B1F3B"/>
    <rect x="2" y="31" width="36" height="4" rx="2" fill="#0B1F3B"/>
    <path d="M7 25L12 17L17 21L22 13L27 17L34 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="34" cy="8" r="2.5" fill="white"/>
  </svg>
);

const WealthIcon = () => (
  <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Bag */}
    <ellipse cx="20" cy="26" rx="13" ry="11" fill="#0B1F3B"/>
    <path d="M15 15c0 0 1-5 5-5s5 5 5 5" stroke="#0B1F3B" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M15 17c1.5-2 8.5-2 10 0" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <text x="20" y="30" textAnchor="middle" fontSize="13" fill="white" fontWeight="bold">$</text>
    {/* Coins stack */}
    <ellipse cx="33" cy="13" rx="5" ry="2.5" fill="white" fillOpacity="0.9"/>
    <ellipse cx="33" cy="10" rx="5" ry="2.5" fill="white" fillOpacity="0.9"/>
    <ellipse cx="33" cy="7" rx="5" ry="2.5" fill="white" fillOpacity="0.9"/>
  </svg>
);

const STATS = [
  { value: 5000, prefix: '', suffix: '', label: 'Members', IconComp: DollarIcon },
  { value: 50, prefix: '', suffix: '+', label: 'Courses', IconComp: BarChartGrowthIcon },
  { value: 1, prefix: '$', suffix: 'M+', label: 'Invested', IconComp: WealthIcon },
  { value: 12, prefix: '', suffix: '', label: 'Events/Year', IconComp: StockMarketIcon },
];

const Counter = ({ value, prefix, suffix, label, IconComp, active }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 2000;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, value]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center">
        <IconComp />
      </div>
      <span className="text-2xl md:text-2xl font-bold text-[#0B1F3B] mt-1.5 mb-0.5" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        {prefix}{count?.toLocaleString()}{suffix}
      </span>
      <span className="text-[#0B1F3B] text-sm font-semibold tracking-widest uppercase">{label}</span>
    </div>
  );
};

const StatsStrip = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.5 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-4 sm:py-6 bg-[#D4A520]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {STATS?.map((stat) => (
            <div
              key={stat?.label}
              className="flex flex-col items-center cursor-default"
              style={{ transition: 'transform 300ms ease-out, filter 300ms ease-out' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.filter = 'drop-shadow(0 4px 12px rgba(212,165,32,0.4))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.filter = 'none';
              }}
            >
              <Counter {...stat} active={active} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsStrip;
